import { ASSIGNED_SEATING } from "@shared/constants";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import SeatingService from "../../../services/SeatingService";
import StageService from "../../../services/StageService";
import { MapPermissionsType } from "../../../types/seating-types";
import {
  REALTIME_SEATING_ACTIONS,
  RealtimeSeatData,
  RealtimeSeatingAction,
  RealtimeSeatingMessage,
} from "../data/types/realtime-seating";

// TODO: Refactor — this file mixes pure parsing with side-effecting orchestration.
//
// Target structure for real-time-seating/:
//
//   utils/
//     realtime-seating-utils.ts      — pure parsing only (parseSeatingMessage, isRecord)
//     realtime-seating-utils.test.ts — parsing tests only
//
//   services/                        — NEW folder
//     realtime-seating-processor.ts  — factory: createRealtimeSeatingProcessor()
//       closes over SeatingService + StageService, owns all orchestration:
//       processMessage, processSingleSeatUpdate, isSeatInUserCart,
//       handleSeatUnavailable, handleSeatAvailable, handleSeatUnassigned,
//       removeSeatPermission, addSeatPermission, updateSeatVisualState,
//       updateSeatVisualStateFromPermissions, findSeatNode,
//       plus KONVA_ATTRS, KONVA_SELECTORS, KonvaNode type
//     realtime-seating-processor.test.ts — plain function tests (no renderHook)
//
//   hooks/
//     useRealtimeSeatingStatus.ts    — updated consumer:
//       const processor = useMemo(
//         () => createRealtimeSeatingProcessor(seatingService, stageService),
//         [seatingService, stageService]
//       );
//       processor.processMessage(message, selectedSeatIds);
//
// Why factory over class: avoids class-vs-hook mental model clash in React,
// testable without renderHook, uses closure instead of `this`.
// Why not a hook: the processor has no React dependencies (no state, no effects),
// so it doesn't need to be a hook — keeps it testable with plain function calls.

const REALTIME_SEATING_ACTION_VALUES = new Set<RealtimeSeatingAction>(
  Object.values(REALTIME_SEATING_ACTIONS)
);
const KONVA_ATTRS = {
  DATA_ID: "id",
  SELECTED: "selected",
  ORIGINAL_FILL: "originalFill",
  FILL: "fill",
  IS_ACTIVE: "isActive",
} as const;
const KONVA_SELECTORS = {
  DISPLAY: ".display",
  LOCATION: ".location",
  LOCATION_DISABLED: ".location_disabled",
} as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function parseSeatingMessage(
  data: Record<string, unknown>
): RealtimeSeatingMessage | null {
  const {
    action,
    basket_id,
    event_id,
    membership_group_id,
    message_id,
    timestamp,
  } = data;

  if (!REALTIME_SEATING_ACTION_VALUES.has(action as RealtimeSeatingAction)) {
    return null;
  }

  if (typeof timestamp !== "string") {
    return null;
  }

  // Must have either event_id or membership_group_id
  const hasEventId = typeof event_id === "number";
  const hasMembershipGroupId = typeof membership_group_id === "number";
  if (!hasEventId && !hasMembershipGroupId) {
    return null;
  }

  if (basket_id !== undefined && typeof basket_id !== "number") {
    return null;
  }

  if (!isRecord(data.data) || !Array.isArray(data.data.seats)) {
    return null;
  }

  return {
    message_id:
      typeof message_id === "string" ? message_id : crypto.randomUUID(),
    action: action as RealtimeSeatingAction,
    data: {
      seats: data.data.seats as RealtimeSeatData[],
    },
    event_id: hasEventId ? event_id : undefined,
    membership_group_id: hasMembershipGroupId ? membership_group_id : undefined,
    timestamp,
    basket_id: typeof basket_id === "number" ? basket_id : undefined,
  };
}

export function processRealtimeSeatingMessage(
  message: RealtimeSeatingMessage,
  seatingService: SeatingService,
  stageService: StageService,
  selectedSeatIds: number[] = []
): void {
  const { action, data } = message;

  data.seats.forEach((seatData) => {
    processSingleSeatUpdate(
      seatData,
      action,
      seatingService,
      stageService,
      selectedSeatIds
    );
  });
}

function processSingleSeatUpdate(
  seatData: RealtimeSeatData,
  action: RealtimeSeatingAction,
  seatingService: SeatingService,
  stageService: StageService,
  selectedSeatIds: number[] = []
): void {
  const { seat_id, permission_id } = seatData;

  if (!seat_id) return;

  if (selectedSeatIds.includes(seat_id)) return;

  if (isSeatInUserCart(seat_id, permission_id, seatingService, stageService)) {
    return;
  }

  switch (action) {
    case REALTIME_SEATING_ACTIONS.SEAT_RESERVED:
      handleSeatUnavailable(seatData, seatingService, stageService);
      break;

    case REALTIME_SEATING_ACTIONS.SEAT_RELEASED:
    case REALTIME_SEATING_ACTIONS.SEAT_ASSIGNED:
      handleSeatAvailable(seat_id, seatData, seatingService, stageService);
      break;

    case REALTIME_SEATING_ACTIONS.SEAT_UNASSIGNED:
      handleSeatUnassigned(seat_id, seatingService, stageService);
      break;

    default:
      break;
  }
}

function isSeatInUserCart(
  seatId: number,
  permissionId: number,
  seatingService: SeatingService,
  stageService: StageService
): boolean {
  const inSeatingObjectArray = seatingService.seatingObjectArray?.some(
    (seat) => seat.id === seatId
  );
  if (inSeatingObjectArray) return true;

  const seatNode = stageService.layers?.seatsAvailable?.findOne(
    (node: { attrs?: { data?: { id?: number } } }) =>
      node.attrs?.data?.[KONVA_ATTRS.DATA_ID] === seatId
  );
  if (seatNode?.getAttr?.(KONVA_ATTRS.SELECTED)) return true;

  if (seatingService.getSeatInCartPermissions(seatId)) return true;
  if (seatingService.getSeatInPendingCartPermissions(seatId)) return true;
  if (seatingService.getCartPermission(permissionId)) return true;

  return false;
}

function handleSeatUnavailable(
  seatData: RealtimeSeatData,
  seatingService: SeatingService,
  stageService: StageService
): void {
  const seatId = seatData.seat_id;
  if (!seatId) return;

  // Remove the reserved permission from the seating map
  removeSeatPermission(seatData, seatingService);

  // When any permission on a seat is reserved, the entire seat becomes unavailable
  // This is because a physical seat can only be occupied by one person
  updateSeatVisualState(seatId, false, stageService);
}

function handleSeatAvailable(
  seatId: number,
  seatData: RealtimeSeatData,
  seatingService: SeatingService,
  stageService: StageService
): void {
  const updatedPermissions = addSeatPermission(
    seatId,
    seatData,
    seatingService
  );

  // Enable listening on seatsDisabled layer so released seats can receive events
  // This is needed because the layer is created with listening: false by default
  if (stageService.layers?.seatsDisabled) {
    stageService.layers.seatsDisabled.listening(true);
  }

  if (updatedPermissions.length > 0) {
    const updated = updateSeatVisualStateFromPermissions(
      seatId,
      updatedPermissions,
      stageService
    );
    if (updated) {
      return;
    }
  }

  updateSeatVisualState(seatId, true, stageService);
}

function handleSeatUnassigned(
  seatId: number,
  seatingService: SeatingService,
  stageService: StageService
): void {
  // eslint-disable-next-line no-param-reassign
  delete seatingService.seatingMap[seatId];
  updateSeatVisualState(seatId, false, stageService);
}

const UNAVAILABLE_SEAT_COLOR =
  ASSIGNED_SEATING.SEAT.STYLE.UNAVAILABLE.colorFill;

type SeatDisplayNode = Parameters<
  StageService["seatFactory"]["updateSeatColor"]
>[0];
type SeatMapNode = Parameters<
  StageService["seatFactory"]["updateSeatColor"]
>[1];

type KonvaNode = {
  name: () => string;
  attrs?: { data?: { id?: number } };
  getAttr: (name: string) => unknown;
  setAttr: (name: string, value: unknown) => void;
  findAncestor: (selector: string) => KonvaNode | null;
  clearCache: () => void;
};

function findSeatNode(
  seatId: number,
  stageService: StageService
): KonvaNode | null {
  // Search in seatsAvailable layer first
  const availableSeats = stageService.layers?.seatsAvailable?.find(
    (node: KonvaNode) => node.name() === ASSIGNED_SEATING.SEAT.ID
  );

  if (availableSeats && availableSeats.length > 0) {
    const node = availableSeats
      .toArray()
      .find(
        (seat: KonvaNode) => seat.attrs?.data?.[KONVA_ATTRS.DATA_ID] === seatId
      );
    if (node) {
      return node as KonvaNode;
    }
  }

  // Also search in seatsDisabled layer (for seats that were unavailable when page loaded)
  const disabledSeats = stageService.layers?.seatsDisabled?.find(
    (node: KonvaNode) => node.name() === ASSIGNED_SEATING.SEAT.ID
  );

  if (disabledSeats && disabledSeats.length > 0) {
    const node = disabledSeats
      .toArray()
      .find(
        (seat: KonvaNode) => seat.attrs?.data?.[KONVA_ATTRS.DATA_ID] === seatId
      );
    if (node) {
      return node as KonvaNode;
    }
  }

  return null;
}

function removeSeatPermission(
  seatData: RealtimeSeatData,
  seatingService: SeatingService
): MapPermissionsType[] {
  const { seat_id, permission_id, ticket_type_id } = seatData;
  if (!seat_id) return [];

  const { seatingMap } = seatingService;
  const existingPermissions = seatingMap[seat_id] ?? [];
  if (existingPermissions.length === 0) {
    return [];
  }

  const filteredPermissions = existingPermissions.filter((permission) => {
    if (permission_id && permission.seatingItemPermissionId === permission_id) {
      return false;
    }

    if (ticket_type_id && permission.seatingItemTypeId === ticket_type_id) {
      return false;
    }

    return true;
  });

  if (filteredPermissions.length === 0) {
    // eslint-disable-next-line no-param-reassign
    delete seatingMap[seat_id];
    return [];
  }

  seatingMap[seat_id] = filteredPermissions;
  return filteredPermissions;
}

function addSeatPermission(
  seatId: number,
  seatData: RealtimeSeatData,
  seatingService: SeatingService
): MapPermissionsType[] {
  const { seatingMap } = seatingService;

  // Event pages key itemTypeMap by ticket_type_id, membership pages by
  // membership_level_id. Mirror the `ticket_type ?? membership_level`
  // pattern used in SeatingService.resetSeatPermissions so real-time
  // updates resolve the correct item type on both page types.
  const itemTypeId =
    seatData.ticket_type_id ?? seatData.membership_level_id ?? null;

  if (!itemTypeId || !seatData.permission_id) {
    return seatingMap[seatId] ?? [];
  }

  const itemType = seatingService.itemTypeMap[itemTypeId];
  if (!itemType) {
    return seatingMap[seatId] ?? [];
  }

  const existingPermissions = seatingMap[seatId] ?? [];
  const hasPermission = existingPermissions.some(
    (permission) =>
      permission.seatingItemPermissionId === seatData.permission_id
  );

  if (hasPermission) {
    return existingPermissions;
  }

  const updatedPermissions = existingPermissions.concat({
    seatingItemTypeId: itemTypeId,
    seatingItemPermissionId: seatData.permission_id,
    seatingItemType: itemType,
  });

  seatingMap[seatId] = updatedPermissions;
  return updatedPermissions;
}

function updateSeatVisualStateFromPermissions(
  seatId: number,
  permissions: MapPermissionsType[],
  stageService: StageService
): boolean {
  try {
    const seatNode = findSeatNode(seatId, stageService);
    if (!seatNode) return false;

    const display = seatNode.findAncestor(KONVA_SELECTORS.DISPLAY);
    if (!display) return false;

    const isAvailable = permissions.length > 0;
    seatNode.setAttr(KONVA_ATTRS.IS_ACTIVE, isAvailable);
    stageService.seatFactory.updateSeatColor(
      display as SeatDisplayNode,
      seatNode as SeatMapNode,
      permissions
    );

    // Find parent location and update its name if needed
    let location = display.findAncestor(KONVA_SELECTORS.LOCATION);
    if (!location) {
      location = display.findAncestor(KONVA_SELECTORS.LOCATION_DISABLED);
      // Rename location_disabled to location so it behaves like active seats
      if (location && isAvailable) {
        location.setAttr("name", "location");
      }
    }

    if (location) {
      location.clearCache();
    }

    stageService.stage?.batchDraw();
    return true;
  } catch (error) {
    recordAppError(error);
    return false;
  }
}

function updateSeatVisualState(
  seatId: number,
  isAvailable: boolean,
  stageService: StageService
): boolean {
  try {
    const seatNode = findSeatNode(seatId, stageService);
    if (!seatNode) return false;

    const display = seatNode.findAncestor(KONVA_SELECTORS.DISPLAY);
    if (!display) return false;

    if (!isAvailable) {
      if (!seatNode.getAttr(KONVA_ATTRS.ORIGINAL_FILL)) {
        seatNode.setAttr(
          KONVA_ATTRS.ORIGINAL_FILL,
          seatNode.getAttr(KONVA_ATTRS.FILL)
        );
      }
      seatNode.setAttr(KONVA_ATTRS.FILL, UNAVAILABLE_SEAT_COLOR);
      seatNode.setAttr(KONVA_ATTRS.IS_ACTIVE, false);

      // Remove wedges for advanced seat colors
      const wedges = (display as SeatDisplayNode).find(".wedge");
      if (wedges && wedges.length > 0) {
        for (let i = 0; i < wedges.length; i += 1) {
          wedges[i].destroy();
        }
      }
    } else {
      const originalFill = seatNode.getAttr(KONVA_ATTRS.ORIGINAL_FILL);
      if (originalFill) {
        seatNode.setAttr(KONVA_ATTRS.FILL, originalFill);
      }
      seatNode.setAttr(KONVA_ATTRS.IS_ACTIVE, true);
    }

    // Find parent location and update its name if needed
    let location = display.findAncestor(KONVA_SELECTORS.LOCATION);
    if (!location) {
      location = display.findAncestor(KONVA_SELECTORS.LOCATION_DISABLED);
      // Rename location_disabled to location so it behaves like active seats
      if (location && isAvailable) {
        location.setAttr("name", "location");
      }
    }

    if (location) {
      location.clearCache();
    }

    stageService.stage?.batchDraw();
    return true;
  } catch (error) {
    recordAppError(error);
    return false;
  }
}
